import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import CloseIcon from "@material-ui/icons/Close";
import DeleteIcon from "@material-ui/icons/Delete";
import Autocomplete from "@material-ui/lab/Autocomplete";
import TextField from "@material-ui/core/TextField";
import { CustomMultiSelect, Input } from "components/Shared/mui-formik-inputs";
import { uniqueZipcodeTourTargets } from "util/zipcodeListGroup";
import { sortSuppliersByName } from "util/sortSuppliers";

const useStyles = makeStyles((theme) => ({
  dialog: {
    "& .MuiDialog-paper": {
      backgroundColor: "#FFFFFF",
      color: "#121212",
      borderRadius: 8,
      maxWidth: 720,
      width: "100%",
    },
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: theme.spacing(2.5, 3, 0),
  },
  title: {
    fontWeight: 700,
    fontSize: 20,
    lineHeight: "28px",
    color: "#333333",
  },
  closeButton: {
    color: "#333333",
    padding: 4,
  },
  content: {
    padding: theme.spacing(2, 3, 1),
  },
  zipcodesBox: {
    border: "1px solid #E0E0E0",
    borderRadius: 4,
    padding: theme.spacing(2),
    marginBottom: theme.spacing(3),
    backgroundColor: "#FAFAFA",
  },
  zipcodesLabel: {
    fontSize: 12,
    lineHeight: "16px",
    color: "#9E9E9E",
    marginBottom: theme.spacing(1.5),
    display: "block",
  },
  zipcodeChip: {
    backgroundColor: "#EEEEEE",
    color: "#121212",
    fontSize: 14,
    fontWeight: 500,
    height: 32,
    borderRadius: 16,
    marginRight: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  fieldsRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(2),
    alignItems: "flex-start",
  },
  fieldColumn: {
    flex: "1 1 200px",
    minWidth: 200,
  },
  filledField: {
    "& .MuiFilledInput-root": {
      backgroundColor: "#F5F5F5",
      borderRadius: 4,
      "&:before, &:after": {
        display: "none",
      },
    },
    "& .MuiFilledInput-root.Mui-disabled": {
      backgroundColor: "#F5F5F5",
    },
    "& .MuiInputLabel-root": {
      color: "#9E9E9E",
    },
    "& .MuiFilledInput-input.Mui-disabled": {
      color: "#121212",
      WebkitTextFillColor: "#121212",
    },
  },
  actions: {
    padding: theme.spacing(2, 3, 2.5),
    justifyContent: "flex-end",
    gap: theme.spacing(2),
  },
  cancelButton: {
    color: "#6F9CEB",
    fontWeight: 500,
    fontSize: 14,
    textTransform: "uppercase",
    padding: theme.spacing(1, 2),
  },
  submitButton: {
    backgroundColor: "#6F9CEB",
    color: "#FFFFFF",
    fontWeight: 600,
    fontSize: 14,
    textTransform: "uppercase",
    borderRadius: 4,
    padding: theme.spacing(1, 2.5),
    boxShadow: "none",
    minWidth: 160,
    "&:hover": {
      backgroundColor: "#5A8AD8",
      boxShadow: "none",
    },
    "&.Mui-disabled": {
      backgroundColor: "#B0C9F5",
      color: "#FFFFFF",
    },
  },
  submitProgress: {
    color: "#FFFFFF",
    marginRight: theme.spacing(1),
  },
}));

function SelectedZipcodesSection({ rows, label, classes }) {
  return (
    <Box className={classes.zipcodesBox}>
      <Typography className={classes.zipcodesLabel}>{label}</Typography>
      <Box display="flex" flexWrap="wrap">
        {rows?.map((row) => (
          <Chip
            key={row.id}
            label={
              row.tourLabel ? `${row.zipcode} (${row.tourLabel})` : String(row.zipcode)
            }
            className={classes.zipcodeChip}
            size="small"
          />
        ))}
      </Box>
    </Box>
  );
}

function ZipcodeBulkReassignModal({
  open,
  title,
  onClose,
  onSubmit,
  submitDisabled,
  submitting,
  selectedRows,
  currentLabel,
  currentValue,
  reassignField,
  isSupplier,
  classes,
  t,
}) {
  const handleDialogClose = (event, reason) => {
    if (submitting) {
      return;
    }
    if (reason === "backdropClick" || reason === "escapeKeyDown") {
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleDialogClose}
      fullWidth
      maxWidth="sm"
      className={classes.dialog}
      disableBackdropClick={submitting}
      disableEscapeKeyDown={submitting}
    >
      <Box className={classes.header}>
        <Typography className={classes.title}>{title}</Typography>
        <IconButton
          className={classes.closeButton}
          size="small"
          onClick={onClose}
          disabled={submitting}
          aria-label="close"
        >
          <CloseIcon />
        </IconButton>
      </Box>
      <DialogContent className={classes.content}>
        <SelectedZipcodesSection
          rows={selectedRows}
          label={`${t("Selected Zipcodes")}:`}
          classes={classes}
        />
        {isSupplier ? (
          <Box className={classes.fieldsRow}>
            <Box className={classes.fieldColumn}>
              <Input
                className={classes.filledField}
                label={currentLabel}
                name="current_value"
                value={currentValue}
                disabled
                errors={{}}
                InputProps={{ readOnly: true }}
              />
            </Box>
            <Box className={classes.fieldColumn}>{reassignField}</Box>
          </Box>
        ) : (
          <Box display="flex" flexDirection="column" style={{ gap: "20px", width: "100%" }}>
            <Box style={{ maxWidth: "320px" }}>
              <Input
                className={classes.filledField}
                label={currentLabel}
                name="current_value"
                value={currentValue}
                disabled
                errors={{}}
                InputProps={{ readOnly: true }}
              />
            </Box>
            <Box style={{ width: "100%" }}>
              {reassignField}
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions className={classes.actions}>
        <Button className={classes.cancelButton} onClick={onClose} disabled={submitting}>
          {t("Cancel")}
        </Button>
        <Button
          className={classes.submitButton}
          variant="contained"
          disableElevation
          disabled={submitDisabled || submitting}
          onClick={onSubmit}
        >
          {submitting && <CircularProgress size={18} className={classes.submitProgress} />}
          {submitting ? t("Updating changes") : t("Update changes")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export function ZipcodesBulkDialogs({
  openSupplier,
  openTour,
  selectedRows,
  supplierList,
  deliveryTours,
  onCloseSupplier,
  onCloseTour,
  onSubmitSupplier,
  onSubmitTour,
}) {
  const { t } = useTranslation("common");
  const classes = useStyles();

  const mode = openSupplier ? "supplier" : openTour ? "tour" : null;
  const open = Boolean(mode);

  const [supplierDraft, setSupplierDraft] = useState([]);
  const [tourMappingsDraft, setTourMappingsDraft] = useState([{ tour_id: null, departures: [] }]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) {
      setSubmitting(false);
    }
  }, [open]);

  useEffect(() => {
    if (mode === "supplier" && selectedRows?.length) {
      setSupplierDraft(selectedRows[0]._supplierIds || []);
    }
  }, [mode, selectedRows]);

  useEffect(() => {
    if (mode === "tour" && selectedRows?.length) {
      const initialMappings = selectedRows[0]._tourMappings?.length
        ? selectedRows[0]._tourMappings.map((m) => ({
            tour_id: m.tour_id,
            departures: m.departures || [],
          }))
        : [
            {
              tour_id: selectedRows[0]._tourId ?? deliveryTours?.[0]?.id ?? null,
              departures: [],
            },
          ];
      setTourMappingsDraft(initialMappings);
    }
  }, [mode, selectedRows, deliveryTours]);

  const currentSupplierLabel = selectedRows?.[0]?.supplierLabel || "";
  const currentTourLabel = selectedRows?.[0]?.tourLabel || "";

  const sortedSupplierList = useMemo(
    () => sortSuppliersByName(supplierList),
    [supplierList],
  );

  const tourOptions = useMemo(
    () =>
      (deliveryTours || []).map((o) => ({
        label: o.name,
        value: o.id,
        disabled: !o.active,
      })),
    [deliveryTours]
  );

  const handleClose = () => {
    if (submitting) {
      return;
    }
    if (mode === "supplier") {
      onCloseSupplier();
    } else if (mode === "tour") {
      onCloseTour();
    }
  };

  const handleSubmit = async () => {
    const zipcode_targets = uniqueZipcodeTourTargets(selectedRows);
    setSubmitting(true);
    try {
      if (mode === "supplier") {
        await onSubmitSupplier({ zipcode_targets, supplier_ids: supplierDraft });
      } else if (mode === "tour") {
        const payload = {
          zipcode_targets,
          tour_id: tourMappingsDraft[0]?.tour_id,
          tour_mappings: tourMappingsDraft.filter((m) => m.tour_id != null),
        };
        await onSubmitTour(payload);
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (!mode) {
    return null;
  }

  const isSupplier = mode === "supplier";
  const title = isSupplier ? t("Reassign Supplier") : t("Reassign Tour");
  const currentLabel = isSupplier ? t("Current Supplier") : t("Current Tour");
  const currentValue = isSupplier ? currentSupplierLabel : currentTourLabel;
  const submitDisabled = isSupplier ? !(supplierDraft && supplierDraft.length) : tourMappingsDraft.some((m) => m.tour_id == null);

  const reassignField = isSupplier ? (
    <CustomMultiSelect
      values={sortedSupplierList}
      errors={{}}
      onChange={(e) => setSupplierDraft(e.target.value)}
      setValues={supplierDraft}
      label={t("Reassign Supplier")}
      labelId="bulk-reassign-suppliers"
      backgroundColor="#F5F5F5"
      borderColor="#D0D0D0"
      labelColor="#9E9E9E"
      required={false}
      name="supplier_ids"
      disabled={submitting}
    />
  ) : (
    <Box display="flex" flexDirection="column" style={{ width: "100%", gap: "16px" }}>
      {tourMappingsDraft.map((mapping, index) => {
        const selectedTour = (deliveryTours || []).find((t) => Number(t.id) === Number(mapping.tour_id));
        const allDepartures = selectedTour
          ? (selectedTour.delivery_timing_configs || [])
            .map((config) => config.delivery_timing)
            .filter(Boolean)
          : [];
        const otherSelectedDepartures = tourMappingsDraft
          .filter((_, i) => i !== index)
          .flatMap((m) => m.departures || []);
        const departures = allDepartures.filter((d) => !otherSelectedDepartures.includes(d));

        const filteredTours = (deliveryTours || []).filter((o) => {
          const tourDepartures = (o.delivery_timing_configs || [])
            .map((config) => config.delivery_timing)
            .filter(Boolean);
          const hasAvailableDeps = tourDepartures.some((d) => !otherSelectedDepartures.includes(d));
          return hasAvailableDeps || Number(o.id) === Number(mapping.tour_id);
        });

        const rowToursOptions = filteredTours.map((o) => ({
          label: o.name,
          value: o.id,
          disabled: !o.active,
        }));

        return (
          <Box key={index} display="flex" alignItems="center" style={{ gap: "12px", width: "100%" }}>
            <Box style={{ flex: 1 }}>
              <Autocomplete
                className={classes.filledField}
                id={`bulk-reassign-tour-${index}`}
                options={rowToursOptions}
                getOptionLabel={(option) => option.label}
                getOptionDisabled={(option) => option.disabled}
                style={{ width: "100%" }}
                value={rowToursOptions.find((o) => o.value === mapping.tour_id) || null}
                onChange={(evt, opt) => {
                  const newMappings = [...tourMappingsDraft];
                  newMappings[index] = {
                    tour_id: opt ? opt.value : null,
                    departures: [],
                  };
                  setTourMappingsDraft(newMappings);
                }}
                disabled={submitting}
                disableClearable
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t("Reassign Tour")}
                    variant="filled"
                    margin="dense"
                    fullWidth
                  />
                )}
              />
            </Box>
            <Box style={{ flex: 1 }}>
              <CustomMultiSelect
                values={departures}
                errors={{}}
                onChange={(e) => {
                  const newMappings = [...tourMappingsDraft];
                  newMappings[index].departures = e.target.value;
                  setTourMappingsDraft(newMappings);
                }}
                setValues={mapping.departures || []}
                label={t("Departures")}
                labelId={`bulk-reassign-tour-departures-${index}`}
                backgroundColor="#F5F5F5"
                borderColor="#D0D0D0"
                labelColor="#9E9E9E"
                required={false}
                name={`departures-${index}`}
                disabled={submitting || !mapping.tour_id}
              />
            </Box>
            <IconButton
              disabled={tourMappingsDraft.length <= 1 || submitting}
              onClick={() => {
                const newMappings = tourMappingsDraft.filter((_, i) => i !== index);
                setTourMappingsDraft(newMappings);
              }}
            >
              <DeleteIcon />
            </IconButton>
          </Box>
        );
      })}
      <Button
        variant="outlined"
        color="primary"
        onClick={() => {
          setTourMappingsDraft([
            ...tourMappingsDraft,
            { tour_id: null, departures: [] },
          ]);
        }}
        disabled={submitting}
        style={{ alignSelf: "flex-start", marginTop: "8px" }}
      >
        {t("Add Tour")}
      </Button>
    </Box>
  );

  return (
    <ZipcodeBulkReassignModal
      open={open}
      title={title}
      onClose={handleClose}
      onSubmit={handleSubmit}
      submitDisabled={submitDisabled}
      submitting={submitting}
      selectedRows={selectedRows}
      currentLabel={currentLabel}
      currentValue={currentValue}
      reassignField={reassignField}
      isSupplier={isSupplier}
      classes={classes}
      t={t}
    />
  );
}
